import {
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  $applyNodeReplacement,
  TextNode,
  LexicalUpdateJSON,
  DOMExportOutput,
} from 'lexical';
import { ProjectMetadataMap } from '../../../const/ProjectMetadata';

export type SerializedProjectMetadataTokenNode = SerializedTextNode & {
  name: string;
  value: string;
  metadataKey: string;
};

// lexical does not export constant IS_TOKEN
export const PROJECT_METADATA_IS_TOKEN = 1;

export class ProjectMetadataTokenNode extends TextNode {
  __name: string;

  __value: string;

  __metadataKey: string;

  static getType(): string {
    return 'token-text';
  }

  static clone(node: ProjectMetadataTokenNode): ProjectMetadataTokenNode {
    return new ProjectMetadataTokenNode(node.__name, node.__value, node.__metadataKey, node.__key);
  }

  constructor(name: string, value: string, metadataKey: string, key?: NodeKey) {
    super(name, key);
    this.__name = name;
    this.__value = value;
    this.__metadataKey = metadataKey;

    this.__mode = PROJECT_METADATA_IS_TOKEN;
  }

  static importJSON(serializedNode: SerializedProjectMetadataTokenNode): ProjectMetadataTokenNode {
    const node = new ProjectMetadataTokenNode(serializedNode.name, serializedNode.value, serializedNode.metadataKey);

    return node.updateFromJSON(serializedNode);
  }

  updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedProjectMetadataTokenNode>): this {
    super.updateFromJSON(serializedNode);
    this.setName(serializedNode.name);
    this.setValue(serializedNode.value);
    this.setMetadataKey(serializedNode.metadataKey);

    return this;
  }

  exportJSON(): SerializedProjectMetadataTokenNode {
    return {
      ...super.exportJSON(),
      name: this.getName(),
      value: this.getValue(),
      text: this.getValue(),
      metadataKey: this.getMetadataKey(),
    };
  }

  setName(name: string): this {
    this.getWritable().__name = name;

    return this;
  }

  getName(): string {
    return this.getLatest().__name;
  }

  setValue(value: string): this {
    this.getWritable().__value = value;

    return this;
  }

  getValue(): string {
    return this.getLatest().__value;
  }

  getMetadataKey(): string {
    return this.getLatest().__metadataKey;
  }

  setMetadataKey(value: string): this {
    this.getWritable().__metadataKey = value;

    return this;
  }

  switchToViewMode(): this {
    this.getWritable().__text = this.getValue();

    return this;
  }

  switchToEditMode(): this {
    this.getWritable().__text = this.getName();

    return this;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createTextNode(this.getName());

    return { element };
  }
}

export function $isProjectMetadataTokenNode(
  node: LexicalNode | null,
): node is ProjectMetadataTokenNode {
  return node instanceof ProjectMetadataTokenNode;
}

export const $createProjectMetadataTokenNode = (
  name: string,
  metadata: Record<string, string>,
): ProjectMetadataTokenNode | null => {
  const param = ProjectMetadataMap[name as keyof typeof ProjectMetadataMap];
  if (!param) {
    return null;
  }

  const node = new ProjectMetadataTokenNode(`{{${name}}}`, metadata[param.key] || '', param.key);

  return $applyNodeReplacement(node);
};
